import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard, MessageSquare, Users, AlertTriangle, CheckCircle,
  Flag, ShieldOff, Eye, RefreshCw, Search, Filter, Package, Plus, Pencil, Trash2, X,
} from 'lucide-react';
import {
  fetchMiNetStats, fetchAllCards, fetchAllChats, flagChatMessage,
  fetchMizaNetPackages, createMizaNetPackage, updateMizaNetPackage, deleteMizaNetPackage,
} from './hooks/hooks';

const NAV  = '#001f3f';
const TEAL = '#0d9488';

// ─── Atoms ────────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
         style={{ backgroundColor: accent + '18' }}>
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Badge = ({ children, color = 'slate' }) => {
  const map = {
    green:  'bg-emerald-100 text-emerald-700 border-emerald-200',
    red:    'bg-red-100 text-red-700 border-red-200',
    amber:  'bg-amber-100 text-amber-700 border-amber-200',
    blue:   'bg-blue-100 text-blue-700 border-blue-200',
    slate:  'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${map[color]}`}>
      {children}
    </span>
  );
};

const fmt = (n) => Number(n ?? 0).toLocaleString();
const timeAgo = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Overview tab ─────────────────────────────────────────────────────────────
const OverviewTab = ({ stats }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard icon={CreditCard}      label="Total Cards"       value={fmt(stats?.total_cards)}       accent={NAV}    sub={`+${fmt(stats?.cards_this_week)} this week`} />
      <StatCard icon={Users}           label="Connections"        value={fmt(stats?.total_connections)} accent={TEAL} />
      <StatCard icon={MessageSquare}   label="Total Messages"     value={fmt(stats?.total_messages)}    accent="#6366f1" sub={`${fmt(stats?.messages_today)} today`} />
      <StatCard icon={AlertTriangle}   label="Flagged Messages"   value={fmt(stats?.flagged_messages)}  accent="#ef4444" />
      <StatCard icon={Flag}            label="Auto-Flagged"       value={fmt(stats?.auto_flagged)}      accent="#f59e0b" />
      <StatCard icon={ShieldOff}       label="Needs Review"       value={fmt(stats?.unreviewed_flags)}  accent="#dc2626" sub="Unreviewed flags" />
    </div>

    {stats?.unreviewed_flags > 0 && (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-700">Action Required</p>
          <p className="text-xs text-red-600 mt-0.5">
            {fmt(stats?.unreviewed_flags)} flagged message{stats?.unreviewed_flags !== 1 ? 's' : ''} waiting for admin review. Go to the Flagged Chats tab to review them.
          </p>
        </div>
      </div>
    )}
  </div>
);

// ─── Cards tab ────────────────────────────────────────────────────────────────
const CardsTab = () => {
  const [search, setSearch] = useState('');
  const { data, isLoading, refetch } = useQuery({ queryKey: ['admin-cards'], queryFn: fetchAllCards });
  const cards = (data?.data || []).filter(c =>
    !search ||
    c.first_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.last_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.organization?.toLowerCase().includes(search.toLowerCase()) ||
    c.owner_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, org or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <button onClick={refetch} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
          <RefreshCw size={15} className="text-slate-500" />
        </button>
        <span className="text-xs text-slate-500">{fmt(data?.total)} total</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Name', 'Owner', 'Organization', 'Template', 'Views', 'Shares', 'Saved By', 'Created'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cards.slice(0, 100).map(c => (
                <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800">{c.first_name} {c.last_name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{c.owner_email}</td>
                  <td className="px-4 py-3 text-slate-600">{c.organization || '—'}</td>
                  <td className="px-4 py-3"><Badge color="blue">{c.template_id}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{fmt(c.views)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(c.shares)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(c.connections)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{timeAgo(c.created_at)}</td>
                </tr>
              ))}
              {cards.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No cards found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Chats tab (all or flagged-only) ─────────────────────────────────────────
const ChatsTab = ({ flaggedOnly = false }) => {
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const qc = useQueryClient();

  const qKey = flaggedOnly ? ['admin-chats-flagged'] : ['admin-chats'];
  const { data, isLoading, refetch } = useQuery({
    queryKey: qKey,
    queryFn:  () => fetchAllChats(flaggedOnly),
  });

  const { mutate: doFlag, isLoading: flagging } = useMutation({
    mutationFn: flagChatMessage,
    onSuccess:  () => { qc.invalidateQueries(['admin-chats']); qc.invalidateQueries(['admin-chats-flagged']); qc.invalidateQueries(['mi-net-stats']); },
  });

  const messages = (data?.data || []).filter(m =>
    !search ||
    m.body?.toLowerCase().includes(search.toLowerCase()) ||
    m.sender_email?.toLowerCase().includes(search.toLowerCase()) ||
    m.receiver_email?.toLowerCase().includes(search.toLowerCase())
  );

  const FLAG_REASONS = ['violence', 'harassment', 'spam', 'hate_speech', 'adult', 'other'];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search messages or emails…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <button onClick={refetch} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
          <RefreshCw size={15} className="text-slate-500" />
        </button>
        {flaggedOnly && (
          <span className="text-xs font-semibold text-red-600 bg-red-50 px-3 py-1.5 rounded-full border border-red-200">
            {fmt(data?.total_flagged)} total flagged
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-2">
          {messages.slice(0, 200).map(m => (
            <div key={m.id}
              className={`bg-white rounded-xl border p-4 transition-colors ${m.is_flagged ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-semibold text-slate-700">{m.sender_email}</span>
                    <span className="text-slate-300">→</span>
                    <span className="text-xs text-slate-500">{m.receiver_email}</span>
                    <span className="text-xs text-slate-400 ml-auto">{timeAgo(m.sent_at)}</span>
                  </div>
                  <p className={`text-sm mt-1 ${m.is_flagged ? 'text-red-800 font-medium' : 'text-slate-700'}`}
                     style={{ whiteSpace: expanded === m.id ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.body}
                  </p>
                  {m.body?.length > 80 && (
                    <button onClick={() => setExpanded(expanded === m.id ? null : m.id)}
                      className="text-xs text-teal-600 hover:underline mt-1">
                      {expanded === m.id ? 'Show less' : 'Show full message'}
                    </button>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {m.is_flagged ? (
                    <>
                      <Badge color="red">
                        <AlertTriangle size={10} />
                        {m.flag_reason || 'Flagged'}
                      </Badge>
                      {m.auto_flagged && <Badge color="amber">Auto</Badge>}
                      {!m.reviewed_by_admin && (
                        <button
                          disabled={flagging}
                          onClick={() => doFlag({ messageId: m.id, action: 'review' })}
                          className="text-xs px-2 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1"
                        >
                          <CheckCircle size={11} /> Clear & Review
                        </button>
                      )}
                      <button
                        disabled={flagging}
                        onClick={() => doFlag({ messageId: m.id, action: 'unflag' })}
                        className="text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                      >
                        Unflag
                      </button>
                    </>
                  ) : (
                    <div className="flex gap-1 flex-wrap justify-end">
                      {FLAG_REASONS.slice(0, 3).map(r => (
                        <button key={r} disabled={flagging}
                          onClick={() => doFlag({ messageId: m.id, action: 'flag', flag_reason: r })}
                          className="text-xs px-2 py-1 border border-slate-200 text-slate-500 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-600">
                          {r}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {messages.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">{flaggedOnly ? 'No flagged messages' : 'No messages found'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Packages tab ─────────────────────────────────────────────────────────────
const EMPTY_PACKAGE_FORM = {
  name: '', price: '', duration_days: '30', portfolio_limit: '4',
  is_unlimited: false, is_default: false, is_active: true, description: '',
};

const PackageForm = ({ initial, onCancel, onSave, saving, error }) => {
  const [form, setForm] = useState(initial || EMPTY_PACKAGE_FORM);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = () => {
    onSave({
      name: form.name,
      price: Number(form.price) || 0,
      duration_days: Number(form.duration_days) || 30,
      portfolio_limit: form.is_unlimited ? null : (Number(form.portfolio_limit) || 0),
      is_default: form.is_default,
      is_active: form.is_active,
      description: form.description,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">{initial ? 'Edit Package' : 'New Package'}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2 rounded-lg">{error}</div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Package name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g. Premium" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Price (TZS)</label>
              <input type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Duration (days)</label>
              <input type="number" min="1" value={form.duration_days} onChange={e => set('duration_days', e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <input type="checkbox" checked={form.is_unlimited} onChange={e => set('is_unlimited', e.target.checked)} />
              Unlimited portfolio uploads
            </label>
            {!form.is_unlimited && (
              <input type="number" min="0" value={form.portfolio_limit} onChange={e => set('portfolio_limit', e.target.value)}
                placeholder="Max portfolio items per card"
                className="w-full mt-2 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <input type="checkbox" checked={form.is_default} onChange={e => set('is_default', e.target.checked)} />
              Default free package
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving || !form.name}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Package'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PackagesTab = () => {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({ queryKey: ['miza-net-packages'], queryFn: fetchMizaNetPackages });
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [formError, setFormError] = useState('');

  const invalidate = () => qc.invalidateQueries(['miza-net-packages']);

  const { mutate: doCreate, isLoading: creating } = useMutation({
    mutationFn: createMizaNetPackage,
    onSuccess: () => { invalidate(); setEditing(null); setFormError(''); },
    onError: (err) => setFormError(err?.response?.data?.error || 'Failed to create package'),
  });

  const { mutate: doUpdate, isLoading: updating } = useMutation({
    mutationFn: updateMizaNetPackage,
    onSuccess: () => { invalidate(); setEditing(null); setFormError(''); },
    onError: (err) => setFormError(err?.response?.data?.error || 'Failed to update package'),
  });

  const { mutate: doDelete } = useMutation({
    mutationFn: deleteMizaNetPackage,
    onSuccess: invalidate,
    onError: (err) => alert(err?.response?.data?.error || 'Failed to delete package'),
  });

  const packages = data?.data || [];

  const handleSave = (values) => {
    if (editing?.id) {
      doUpdate({ packageId: editing.id, ...values });
    } else {
      doCreate(values);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Free is the default tier every user starts on. Paid tiers raise or remove the portfolio-upload cap.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={refetch} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <RefreshCw size={15} className="text-slate-500" />
          </button>
          <button onClick={() => { setFormError(''); setEditing({}); }}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700">
            <Plus size={14} /> New Package
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Name', 'Price', 'Duration', 'Portfolio Limit', 'Default', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {packages.map(p => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-800">{p.name}</td>
                  <td className="px-4 py-3 text-slate-600">{Number(p.price) > 0 ? `TZS ${fmt(p.price)}` : 'Free'}</td>
                  <td className="px-4 py-3 text-slate-600">{p.duration_days} days</td>
                  <td className="px-4 py-3 text-slate-600">{p.is_unlimited ? 'Unlimited' : p.portfolio_limit}</td>
                  <td className="px-4 py-3">{p.is_default && <Badge color="blue">Default</Badge>}</td>
                  <td className="px-4 py-3">
                    <Badge color={p.is_active ? 'green' : 'slate'}>{p.is_active ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setFormError(''); setEditing(p); }}
                        className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
                        <Pencil size={13} className="text-slate-500" />
                      </button>
                      <button onClick={() => { if (window.confirm(`Delete "${p.name}"?`)) doDelete(p.id); }}
                        className="p-1.5 rounded-lg border border-slate-200 hover:bg-red-50">
                        <Trash2 size={13} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {packages.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No packages yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <PackageForm
          initial={editing.id ? {
            name: editing.name,
            price: String(editing.price),
            duration_days: String(editing.duration_days),
            portfolio_limit: String(editing.portfolio_limit ?? '4'),
            is_unlimited: editing.is_unlimited,
            is_default: editing.is_default,
            is_active: editing.is_active,
            description: editing.description || '',
          } : null}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={creating || updating}
          error={formError}
        />
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'cards',    label: 'All Cards' },
  { key: 'packages', label: 'Packages' },
  { key: 'chats',    label: 'All Chats' },
  { key: 'flagged',  label: 'Suspected Chats', alert: true },
];

export default function MiNetAdmin() {
  const [tab, setTab] = useState('overview');
  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['mi-net-stats'],
    queryFn:  fetchMiNetStats,
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Miza Net</h2>
          <p className="text-xs text-slate-400 mt-0.5">Digital cards, connections & chat moderation</p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors
              ${tab === t.key
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
            {t.alert && (stats?.unreviewed_flags > 0) && (
              <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">
                {stats.unreviewed_flags}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading && tab === 'overview' ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <>
          {tab === 'overview' && <OverviewTab stats={stats} />}
          {tab === 'cards'    && <CardsTab />}
          {tab === 'packages' && <PackagesTab />}
          {tab === 'chats'    && <ChatsTab flaggedOnly={false} />}
          {tab === 'flagged'  && <ChatsTab flaggedOnly={true} />}
        </>
      )}
    </div>
  );
}
